import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination
} from '@material-ui/core';
import { TABLE_HEADER_STYLE, TABLE_CELL_STYLE, TABLE_PAGINATION_STYLE, TABLE_SORT_LABEL_STYLE } from 'utils/tableStyles';
import { logAuditTrailEvent } from 'services/system_setup/auditTrail';
// import { fetchInvoiceDrAPI } from 'services/invoice/invoice'

const columns = [
    {
        title: "INVOICE NO.",
        dataIndex: "invoice_no",
        sortable: true,
        key: "invoice_no",
        width: "15%"
    },
    {
        title: "INVOICE DATE",
        dataIndex: "formated_inv_date",
        sortable: true,
        key: "invoice_date",
        width: "15%"
    },
    {
        title: "CUSTOMER",
        dataIndex: "customer",
        key: "customer_id",
        width: "40%"
    },
    {
        title: "AGENT",
        dataIndex: "agent",
        key: "agent_id",
        width: "20%"
    },
    {
        title: "STATUS",
        dataIndex: "status_name",
        key: "status_id",
        width: "10%"
    }
];

function parseDateValue(value) {
    if (!value) return NaN;
    if (value instanceof Date) return value.getTime();

    const normalized = String(value).trim();
    if (!normalized) return NaN;

    const parsed = Date.parse(normalized);
    if (!Number.isNaN(parsed)) return parsed;

    const mdy = normalized.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (mdy) {
        const month = Number(mdy[1]) - 1;
        const day = Number(mdy[2]);
        const year = Number(mdy[3]);
        return new Date(year, month, day).getTime();
    }

    return NaN;
}

function SearchTable(props) {
    const history = useHistory();
    const { data = [], pageSize = 10 } = props;

    const [orderBy, setOrderBy] = useState('formated_inv_date');
    const [order, setOrder] = useState('desc');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pageSize);

    // Sync rowsPerPage with pageSize prop and reset page when pageSize changes
    useEffect(() => {
        setRowsPerPage(pageSize);
        setPage(0);
    }, [pageSize]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedData = useMemo(() => {
        const copy = [...data];
        if (!orderBy) return copy;
        return copy.sort((a, b) => {
            if (orderBy === 'formated_inv_date') {
                const aDate = parseDateValue(a.formated_inv_date || a.formatted_inv_date || a.invoice_date);
                const bDate = parseDateValue(b.formated_inv_date || b.formatted_inv_date || b.invoice_date);

                if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
                    return order === 'asc' ? aDate - bDate : bDate - aDate;
                }
            }

            const av = (a[orderBy] || '').toString();
            const bv = (b[orderBy] || '').toString();
            const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
            return order === 'asc' ? cmp : -cmp;
        });
    }, [data, orderBy, order]);

    const pagedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleOpenRecord = (row) => {
        void logAuditTrailEvent({
            moduleCode: 'inv',
            subModuleCode: 'search',
            action: 'VIEW',
            description: `View invoice record ${row && row.invoice_no ? row.invoice_no : ''}`.trim(),
            payload: {
                invoice_id: row && row.invoice_id,
                invoice_no: row && row.invoice_no,
            },
            isSuccess: 1,
        });

        history.push({
            pathname: '/invoice/details_update',
            state: { record: row },
        });
    };

    return (
        <div>
            <TableContainer component={Paper}>
                <MuiTable>
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell 
                                    key={col.key || col.dataIndex} 
                                    style={{ ...TABLE_HEADER_STYLE, width: col.width }}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === col.dataIndex}
                                            direction={orderBy === col.dataIndex ? order : 'asc'}
                                            onClick={() => handleRequestSort(col.dataIndex)}
                                            style={TABLE_SORT_LABEL_STYLE}
                                        >
                                            {col.title}
                                        </TableSortLabel>
                                    ) : (
                                        col.title
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map((row, idx) => (
                            <TableRow
                                key={row.key || row.invoice_no || idx}
                                hover
                                onDoubleClick={() => handleOpenRecord(row)}
                            >
                                {columns.map((col) => (
                                    <TableCell 
                                        key={col.key || col.dataIndex}
                                        style={TABLE_CELL_STYLE}
                                    >
                                        {row[col.dataIndex]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {pagedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center" style={TABLE_CELL_STYLE}>
                                    No records found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </MuiTable>
            </TableContainer>
            <TablePagination
                component="div"
                count={data.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[]}
                style={TABLE_PAGINATION_STYLE}
            />
        </div>
    );
}

export default SearchTable;
